import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Plus, Save, Trash2, Layers, ArrowLeft, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_TYPES = ["Mock", "Magazine"];

const SectionTemplates = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [editing, setEditing] = useState(null); // template being edited or {isNew:true}
  const [editExam, setEditExam] = useState("");
  const [editProductType, setEditProductType] = useState("Mock");
  const [editSections, setEditSections] = useState([]);
  const [newSectionInput, setNewSectionInput] = useState("");
  const [saving, setSaving] = useState(false);

  const canEdit = user?.role === "admin" || user?.role === "content_manager";

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/section-templates`);
      setTemplates(res.data || []);
    } catch (e) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const grouped = useMemo(() => {
    const filtered = filterType === "all" ? templates : templates.filter((t) => t.product_type === filterType);
    const byType = { Mock: [], Magazine: [] };
    filtered.forEach((t) => {
      if (!byType[t.product_type]) byType[t.product_type] = [];
      byType[t.product_type].push(t);
    });
    Object.keys(byType).forEach((k) => byType[k].sort((a, b) => a.exam.localeCompare(b.exam)));
    return byType;
  }, [templates, filterType]);

  const openEdit = (tpl) => {
    setEditing(tpl);
    setEditExam(tpl.exam);
    setEditProductType(tpl.product_type);
    setEditSections([...(tpl.sections || [])]);
    setNewSectionInput("");
  };

  const openNew = () => {
    setEditing({ isNew: true });
    setEditExam("");
    setEditProductType("Mock");
    setEditSections([]);
    setNewSectionInput("");
  };

  const addSection = () => {
    const s = (newSectionInput || "").trim();
    if (!s) return;
    if (editSections.some((x) => x.toLowerCase() === s.toLowerCase())) {
      toast.error("Section already exists");
      return;
    }
    setEditSections([...editSections, s]);
    setNewSectionInput("");
  };

  const removeSection = (idx) => {
    setEditSections(editSections.filter((_, i) => i !== idx));
  };

  const moveSection = (idx, dir) => {
    const next = [...editSections];
    const tgt = idx + dir;
    if (tgt < 0 || tgt >= next.length) return;
    [next[idx], next[tgt]] = [next[tgt], next[idx]];
    setEditSections(next);
  };

  const saveTemplate = async () => {
    const exam = (editExam || "").trim();
    if (!exam) { toast.error("Exam name is required"); return; }
    if (!editSections.length) { toast.error("At least one section is required"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/section-templates`, {
        exam,
        product_type: editProductType,
        sections: editSections,
      });
      toast.success("Template saved");
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete the ${tpl.exam} ${tpl.product_type} template?`)) return;
    try {
      await axios.delete(`${API}/section-templates/${tpl.id}`);
      toast.success("Template deleted");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete template");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl" data-testid="section-templates-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to="/app/settings" className="inline-flex items-center text-xs text-slate-500 hover:text-slate-800 mb-2">
            <ArrowLeft size={14} /> Back to Settings
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Layers size={22} className="text-prepify-600" /> Section Templates
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Configure the sections that Mocks and Magazines are divided into, per exam. Each section in a template becomes an individually-assignable task.
          </p>
        </div>
        {canEdit && (
          <Button onClick={openNew} data-testid="new-template-btn">
            <Plus size={16} /> New template
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Label className="text-sm">Filter:</Label>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48" data-testid="filter-product-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="Mock">Mock</SelectItem>
            <SelectItem value="Magazine">Magazine</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-slate-500">Loading templates…</div>
      ) : (
        <div className="space-y-8">
          {PRODUCT_TYPES.map((pt) => {
            if (filterType !== "all" && filterType !== pt) return null;
            const list = grouped[pt] || [];
            return (
              <div key={pt}>
                <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">
                  {pt} Templates
                </h2>
                {list.length === 0 ? (
                  <div className="text-sm text-slate-400 bg-slate-50 rounded-lg p-4">No {pt} templates.</div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-3">
                    {list.map((tpl) => (
                      <div key={tpl.id} className="border border-slate-200 rounded-xl bg-white p-4 hover:border-prepify-300 transition-colors" data-testid={`tpl-${tpl.product_type}-${tpl.exam}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="text-base font-semibold text-slate-900">{tpl.exam}</div>
                            <div className="text-xs text-slate-500">{tpl.sections.length} section{tpl.sections.length !== 1 ? "s" : ""}</div>
                          </div>
                          <div className="flex gap-1">
                            {canEdit && (
                              <>
                                <button
                                  onClick={() => openEdit(tpl)}
                                  className="text-xs px-2.5 py-1 rounded text-prepify-700 hover:bg-prepify-50"
                                  data-testid={`edit-tpl-${tpl.id}`}
                                >Edit</button>
                                {user?.role === "admin" && (
                                  <button
                                    onClick={() => deleteTemplate(tpl)}
                                    className="text-xs px-2.5 py-1 rounded text-red-600 hover:bg-red-50 inline-flex items-center gap-1"
                                    data-testid={`delete-tpl-${tpl.id}`}
                                  ><Trash2 size={12} /></button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {tpl.sections.map((s) => (
                            <span key={s} className="text-xs px-2 py-0.5 rounded bg-prepify-50 text-prepify-700 border border-prepify-100">
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Edit / New Dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-xl" data-testid="template-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.isNew ? "New template" : `Edit ${editing?.exam || ""} ${editing?.product_type || ""} template`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Exam</Label>
                <Input
                  value={editExam}
                  onChange={(e) => setEditExam(e.target.value)}
                  placeholder="e.g., CLAT"
                  disabled={!editing?.isNew}
                  data-testid="edit-tpl-exam"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Product Type</Label>
                <Select value={editProductType} onValueChange={setEditProductType} disabled={!editing?.isNew}>
                  <SelectTrigger data-testid="edit-tpl-product-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRODUCT_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Sections</Label>
              <div className="flex items-center gap-2">
                <Input
                  value={newSectionInput}
                  onChange={(e) => setNewSectionInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSection(); } }}
                  placeholder="Add a section (e.g., English) and press Enter"
                  data-testid="edit-tpl-new-section"
                />
                <Button type="button" variant="outline" onClick={addSection} data-testid="edit-tpl-add-section">
                  <Plus size={14} />
                </Button>
              </div>
              {editSections.length === 0 ? (
                <div className="text-xs text-slate-400 mt-1">Add one or more sections.</div>
              ) : (
                <div className="mt-2 border border-slate-100 rounded-lg divide-y divide-slate-100">
                  {editSections.map((s, idx) => (
                    <div key={`${s}-${idx}`} className="flex items-center justify-between px-3 py-2" data-testid={`edit-tpl-section-row-${idx}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 font-mono w-5">{idx + 1}.</span>
                        <span className="text-sm text-slate-800">{s}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="text-xs px-1.5 text-slate-400 hover:text-slate-800 disabled:opacity-30">↑</button>
                        <button type="button" onClick={() => moveSection(idx, 1)} disabled={idx === editSections.length - 1} className="text-xs px-1.5 text-slate-400 hover:text-slate-800 disabled:opacity-30">↓</button>
                        <button type="button" onClick={() => removeSection(idx)} className="text-xs text-red-500 hover:text-red-700" data-testid={`edit-tpl-remove-section-${idx}`}>
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="button" onClick={saveTemplate} disabled={saving} data-testid="save-tpl-btn">
              <Save size={14} /> {saving ? "Saving…" : "Save template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SectionTemplates;
